import { PlusIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useState } from 'react';
import type { ObjectParam } from 'shared-types';
import { T, useT } from '../../../translation/src';
import { addParam, uniqueParamKey } from '../document/specOps';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { PanelButton, PanelSection } from './fields';

type ParamPreset = 'length' | 'number' | 'count' | 'switch' | 'choice';

/** What a new param of each kind starts as (key/label are made unique by the caller). */
function presetParam(preset: ParamPreset, key: string, label: string): ObjectParam {
  switch (preset) {
    case 'length':
      return { key, label, kind: 'float', default: 0.6, min: 0.05, max: 5, decimals: 2, distance: true };
    case 'number':
      return { key, label, kind: 'float', default: 1, decimals: 2 };
    case 'count':
      return { key, label, kind: 'int', default: 2, min: 1, max: 100 };
    case 'switch':
      return { key, label, kind: 'boolean', default: true };
    case 'choice':
      return {
        key,
        label,
        kind: 'select',
        default: 'a',
        options: [
          { value: 'a', label: 'Option A' },
          { value: 'b', label: 'Option B' },
        ],
      };
  }
}

export const describeParamDefault = (p: ObjectParam): string => {
  switch (p.kind) {
    case 'float':
      return p.distance ? `${p.default} m` : String(p.default);
    case 'int':
      return String(p.default);
    case 'boolean':
      return p.default ? 'on' : 'off';
    case 'select':
      return p.options.find((o) => o.value === p.default)?.label ?? p.default;
  }
};

/**
 * The object's parameters — what a user can set on a placed object in the
 * properties panel (step size, number of doors, show swings…). Repeats and
 * conditional groups bind to them.
 */
const ParamsPanel_: React.FC = () => {
  const { store } = useObjectEditor();
  const { spec, selection } = useEditorState();
  const [menuOpen, setMenuOpen] = useState(false);
  const presets: Array<{ id: ParamPreset; label: string; description: string }> = [
    { id: 'length', label: useT('length', { swc: true }), description: useT('a distance, e.g. step size or door width', { swc: true }) },
    { id: 'count', label: useT('count', { swc: true }), description: useT('a whole number, e.g. number of doors', { swc: true }) },
    { id: 'switch', label: useT('on/off', { swc: true }), description: useT('shows or hides parts, e.g. door swings', { swc: true }) },
    { id: 'choice', label: useT('choice', { swc: true }), description: useT('pick one of a few variants', { swc: true }) },
    { id: 'number', label: useT('number', { swc: true }), description: useT('any other number', { swc: true }) },
  ];

  const create = (preset: ParamPreset, label: string) => {
    const key = uniqueParamKey(spec, label);
    store.apply((s) => addParam(s, presetParam(preset, key, label.toLowerCase())), { kind: 'param', key });
    setMenuOpen(false);
  };

  return (
    <PanelSection
      title={<T _str="parameters" />}
      actions={
        <div className="relative">
          <PanelButton onClick={() => setMenuOpen((o) => !o)}>
            <PlusIcon className="h-3.5 w-3.5" />
            <T _str="add" swc />
          </PanelButton>
          {menuOpen && (
            <ul className="absolute right-0 top-8 z-30 w-64 rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-eerie-black">
              {presets.map((p) => (
                <li key={p.id}>
                  <button type="button" onClick={() => create(p.id, p.label)} className="flex w-full flex-col rounded px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-black">
                    <span className="text-sm font-medium">{p.label}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{p.description}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      }
    >
      {spec.params.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <T _str="parameters let users adjust a placed object, and drive repeats and optional parts" swc />
        </p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {spec.params.map((p) => (
            <li key={p.key}>
              <button
                type="button"
                onClick={() => store.select({ kind: 'param', key: p.key })}
                className={classNames(
                  'flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm',
                  selection.kind === 'param' && selection.key === p.key ? 'bg-neon-green-300 text-black' : 'hover:bg-gray-100 dark:hover:bg-black',
                )}
              >
                <span className="truncate">{p.label}</span>
                <span className="shrink-0 text-xs text-gray-500">{describeParamDefault(p)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </PanelSection>
  );
};

export const ParamsPanel = memo(ParamsPanel_);
